// Value-source resolution and the selection-equality predicate for
// OptionSelectItem.
//
// Co-located with its only consumer, and deliberately import-free: `yarn
// test:unit` runs the sibling test under bare `node --test`, which cannot
// resolve `@render/*` at runtime and refuses `enum` — so nothing here may
// import ./types or any alias at value level. The one type-only import is
// erased.
import type { OfferGroupDataDto } from '@api/types.gen'

const REF_PREFIXES = ['var.', 'const.', 'tr.'] as const

function isReference(value: unknown): boolean {
  return value !== null && typeof value === 'object' && 'field' in (value as object)
}

/**
 * The prefixed key inside a `{field: …}` operand, or undefined when there is
 * nothing usable.
 *
 * A bare prefix (`'const.'`) counts as nothing: the picker writes exactly that
 * the instant its prefix dropdown changes, before any key is typed. Treating it
 * as "not configured" is what lets the editor surface a hint instead of
 * silently rendering an item that can never match.
 */
export function operandRefKey(value: unknown): string | undefined {
  if (!isReference(value)) return undefined
  const field = (value as { field: unknown }).field
  const raw =
    typeof field === 'string'
      ? field
      : Array.isArray(field) && typeof field[0] === 'string'
        ? field[0]
        : undefined
  if (!raw) return undefined
  if ((REF_PREFIXES as readonly string[]).includes(raw)) return undefined
  return raw
}

/** True when the author picked a reference arm rather than a raw literal. */
export function isOperandReference(value: unknown): boolean {
  return isReference(value)
}

/**
 * Resolve a value source to the value it stands for. A reference is read at
 * call time so the caller stores the CURRENT value of the source rather than
 * the wrapper — downstream code reads deep paths off the target variable
 * (`regular.ppoId` under a selected offer group), which a stored wrapper would
 * break.
 */
export function resolveOperandValue(value: unknown, getData: (key: string) => unknown): unknown {
  if (!isReference(value)) return value
  const key = operandRefKey(value)
  return key ? getData(key) : undefined
}

const isOfferGroup = (v: unknown): v is OfferGroupDataDto =>
  !!v && typeof v === 'object' && (v as { type?: unknown }).type === 'offer_group_value'

const samePrice = (a: unknown, b: unknown): boolean =>
  a === undefined && b === undefined ? true : Number(a) === Number(b)

/** Own keys whose value is not `undefined`. */
function definedKeys(o: Record<string, unknown>): string[] {
  return Object.keys(o).filter((k) => o[k] !== undefined)
}

/**
 * Structural equality.
 *
 * Undefined-valued keys are ignored on both sides: the backend emits an
 * explicit `undefined` for absent optional offer fields and `JSON.stringify`
 * drops them on the way into localStorage, so a key-count comparison reports a
 * reloaded object as different from the constant it came from.
 */
function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false
  const aIsArr = Array.isArray(a)
  if (aIsArr !== Array.isArray(b)) return false
  if (aIsArr) {
    const aa = a as unknown[]
    const bb = b as unknown[]
    return aa.length === bb.length && aa.every((v, i) => deepEqual(v, bb[i]))
  }
  const ao = a as Record<string, unknown>
  const bo = b as Record<string, unknown>
  const ka = definedKeys(ao)
  if (ka.length !== definedKeys(bo).length) return false
  return ka.every((k) => k in bo && deepEqual(ao[k], bo[k]))
}

/**
 * "Is the stored value the one this item stands for?" — the value can be of any
 * type, so `===` is not enough.
 *
 * - `null` / `undefined` never match anything, including each other. `null` is
 *   what a deselect writes, so it must not read back as a selection.
 * - An offer group is identified by its plan, not its record: a value
 *   round-tripped through localStorage is a fresh object and can never be
 *   reference-equal to the release constant. `ppoId` alone is not enough —
 *   two offer groups built from different offers on the same price-product
 *   object carry the same `ppoId` and the same `apiBasePrice`, so the
 *   offer-level `basePricePerPeriod` is what separates them.
 * - A number and a string compare by their string form, so a `2` authored here
 *   still matches a `'2'` restored from a query param or a release default.
 * - Everything else is strict. This deliberately does NOT reproduce the
 *   visibility-rule `==` comparator, which treats a literal `false` as "falsy"
 *   and compares arrays as unordered multisets — correct for a rule, wrong for
 *   a selection test, where `0` must not match `''` or `false`.
 */
export function valuesMatch(a: unknown, b: unknown): boolean {
  if (a === undefined || a === null || b === undefined || b === null) return false

  if (isOfferGroup(a) && isOfferGroup(b)) {
    return (
      a.regular?.ppoId === b.regular?.ppoId &&
      a.exit?.ppoId === b.exit?.ppoId &&
      samePrice(a.regular?.basePricePerPeriod, b.regular?.basePricePerPeriod)
    )
  }

  if (typeof a === 'object' || typeof b === 'object') return deepEqual(a, b)

  const aScalar = typeof a === 'number' || typeof a === 'string'
  const bScalar = typeof b === 'number' || typeof b === 'string'
  if (aScalar && bScalar) return String(a) === String(b)

  return a === b
}
